/**
 * lib/orgAccess.ts — "what can this person actually do right now?", derived.
 *
 * ── WHY A ROSTER ROW SAYS THIS AND NOT "joined Mar 2026" ──────────────────
 * CLAUDE.md invariant 3 is the product: prices, `refStockId` and supplier
 * contact are revealed only to a caller who is signed in AND KYC-approved. And
 * docs/ORG-ACCOUNTS.md decides that **the ORG is approved and members inherit
 * it** — which is the entire reason a seat is worth paying for, and is
 * currently invisible to every user in the product.
 *
 * So every roster row's second line is not a join date. It is what that person
 * can see today. Do that on three rows and the paywall never has to *argue*
 * "everyone inherits your verification" — the owner has been watching it
 * happen. It converts a marketing claim into an observation, from data two
 * existing endpoints already return.
 *
 * ── THE HONESTY RULES (these are the failure mode of this whole idea) ─────
 * 1. **Present tense, never a promise.** "Sees prices", never "will see
 *    prices". This module is client-side and `shared/identity.ts` is the
 *    authority; if they ever disagree, a descriptive sentence is wrong for
 *    60 seconds and a promise is wrong forever.
 * 2. **One function, three consumers.** The row, the meter segment and the
 *    screen-reader label all come from here, so they cannot disagree with
 *    each other even when they are all wrong together.
 * 3. **No abbreviations.** A blind user gets prose, not "EX EX".
 */

import type { OrgFull, OrgInviteRow, OrgMemberRow } from '../api/endpoints'
import { ApiError } from '../api/types'

export type OrgKycStatus = 'not_submitted' | 'submitted' | 'approved' | 'rejected'

/** Which material the seat meter draws for this row. See `.seat-seg--*`. */
export type SeatSegmentKind = 'used' | 'reserved' | 'paused' | 'next' | 'free'

export interface AccessVerdict {
  /** The sentence a human reads. Prose, present tense. */
  sentence: string
  /** Colour intent — the row picks a class, never a colour, from this. */
  tone: 'normal' | 'muted' | 'warning'
  /** The meter material this person is holding, if any. */
  segment: SeatSegmentKind
  /** Does this row hold a seat against the limit right now? */
  holdsSeat: boolean
}

/* ─────────────────────────── relative time ─────────────────────────────── */

/**
 * "2 days ago" / "in 5 days". Deliberately coarse: the underlying
 * `lastSeenAt`-style fields are written at low resolution, and rendering
 * "4 minutes ago" over 5-minute data is a fabricated precision.
 * Falls back to '' (never "Invalid Date") so a malformed timestamp degrades to
 * a shorter sentence instead of a broken one.
 */
export function relativeTime(iso: string | null | undefined, nowMs: number): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return ''
  const deltaMs = t - nowMs
  const future = deltaMs > 0
  const abs = Math.abs(deltaMs)
  const mins = Math.round(abs / 60000)
  const hours = Math.round(abs / 3600000)
  const days = Math.round(abs / 86400000)
  let unit: string
  if (abs < 90000) unit = 'a moment'
  else if (mins < 60) unit = `${mins} minute${mins === 1 ? '' : 's'}`
  else if (hours < 36) unit = `${hours} hour${hours === 1 ? '' : 's'}`
  else if (days < 45) unit = `${days} day${days === 1 ? '' : 's'}`
  else {
    const months = Math.round(days / 30)
    unit = `${months} month${months === 1 ? '' : 's'}`
  }
  return future ? `in ${unit}` : `${unit} ago`
}

/* ───────────────────────────── the verdict ─────────────────────────────── */

export function orgKycStatusOf(org: Pick<OrgFull, 'kyc'> | null | undefined): OrgKycStatus {
  return org?.kyc?.status ?? 'not_submitted'
}

/**
 * A MEMBER row's access sentence.
 *
 * `orgApproved` is the org's KYC, not the member's: an active member of an
 * approved org sees prices without a review of their own. That inheritance is
 * the product.
 */
export function memberAccess(
  member: Pick<OrgMemberRow, 'status' | 'seatCountsAgainstLimit'>,
  orgKyc: OrgKycStatus,
): AccessVerdict {
  if (member.status === 'suspended') {
    // We deliberately do NOT guess *why*. The server does not send a
    // suspension reason on `orgMemberView`, and inventing "your card failed"
    // for what might be a rejected KYC is worse than saying less.
    return {
      sentence: 'Browsing with prices locked — seat paused',
      tone: 'warning',
      segment: 'paused',
      holdsSeat: false,
    }
  }
  if (orgKyc === 'approved') {
    return {
      sentence: 'Sees prices · supplier contacts',
      tone: 'normal',
      segment: 'used',
      holdsSeat: member.seatCountsAgainstLimit !== false,
    }
  }
  if (orgKyc === 'rejected') {
    return {
      sentence: 'Locked — your company’s verification was declined',
      tone: 'warning',
      segment: 'used',
      holdsSeat: member.seatCountsAgainstLimit !== false,
    }
  }
  return {
    sentence: 'Waiting on your company’s verification',
    tone: 'muted',
    segment: 'used',
    holdsSeat: member.seatCountsAgainstLimit !== false,
  }
}

/**
 * A PENDING INVITE's sentence. An invite is not a person: it holds a seat and
 * grants nothing, and the row has to say both or the support ticket is "why
 * can't Amit sign in?".
 */
export function inviteAccess(
  invite: Pick<OrgInviteRow, 'status' | 'createdAt' | 'expiresAt'>,
  nowMs: number,
): AccessVerdict {
  const expiresAt = invite.expiresAt ? Date.parse(invite.expiresAt) : NaN
  const expired = invite.status === 'expired'
    || (Number.isFinite(expiresAt) && expiresAt <= nowMs)
  if (expired) {
    const when = relativeTime(invite.expiresAt, nowMs)
    return {
      sentence: when ? `Invite expired ${when}` : 'Invite expired',
      tone: 'muted',
      segment: 'free',
      holdsSeat: false,
    }
  }
  const sent = relativeTime(invite.createdAt, nowMs)
  const gone = relativeTime(invite.expiresAt, nowMs)
  const head = sent ? `Invited ${sent}` : 'Invited'
  const tail = gone ? ` · expires ${gone}` : ''
  return {
    sentence: `${head} — can’t see anything yet${tail}`,
    tone: 'muted',
    segment: 'reserved',
    holdsSeat: true,
  }
}

/* ────────────────────────────── the meter ──────────────────────────────── */

/** The most segments we draw before the sentence has to carry it. Past this
 *  the metaphor is decoration, so we stop scaling it (spec §4.6). */
export const MAX_METER_SEGMENTS = 12

export interface MeterSegment {
  key: string
  kind: SeatSegmentKind
}

export interface MeterModel {
  segments: MeterSegment[]
  /** How many were NOT drawn — rendered as a "+N" label, not a thinner bar. */
  overflow: number
  /** The screen-reader sentence. The pips themselves are decorative. */
  label: string
}

/**
 * used + reserved + paused + free, capped, with the honest overflow count.
 *
 * `paused` seats are drawn but do NOT count against the limit — a suspended
 * member holds no seat (`memberOccupiesSeat` server-side), and hatching them
 * is how an owner reads "paused" instead of "free".
 */
export function meterModel(a: {
  used: number
  pendingInvites: number
  paused: number
  limit: number
}): MeterModel {
  const used = Math.max(0, Math.trunc(a.used || 0))
  const reserved = Math.max(0, Math.trunc(a.pendingInvites || 0))
  const paused = Math.max(0, Math.trunc(a.paused || 0))
  const limit = Math.max(0, Math.trunc(a.limit || 0))
  const free = Math.max(0, limit - used - reserved)

  const wanted: SeatSegmentKind[] = [
    ...Array<SeatSegmentKind>(used).fill('used'),
    ...Array<SeatSegmentKind>(reserved).fill('reserved'),
    ...Array<SeatSegmentKind>(paused).fill('paused'),
    // The FIRST free slot is the one you could buy into; the rest are plain.
    ...(free > 0 ? (['next', ...Array<SeatSegmentKind>(free - 1).fill('free')] as SeatSegmentKind[]) : []),
  ]
  const segments = wanted.slice(0, MAX_METER_SEGMENTS)
    .map((kind, i) => ({ key: `${kind}-${i}`, kind }))

  const parts = [`${used} in use`]
  if (reserved > 0) parts.push(`${reserved} held by ${reserved === 1 ? 'a pending invite' : 'pending invites'}`)
  if (paused > 0) parts.push(`${paused} paused`)
  parts.push(`${limit} total`)

  return {
    segments,
    overflow: Math.max(0, wanted.length - segments.length),
    label: `Seats: ${parts.join(', ')}.`,
  }
}

/**
 * The headline above the roster — the sentence that makes the paywall
 * unnecessary to argue.
 */
export function accessHeadline(a: {
  orgKyc: OrgKycStatus
  orgName: string
  activeCount: number
  pausedCount: number
}): string {
  const { activeCount: n, pausedCount: m } = a
  if (a.orgKyc !== 'approved') {
    const who = a.orgName?.trim() || 'your company'
    return a.orgKyc === 'rejected'
      ? `Nobody sees prices — ${who}’s verification was declined.`
      : `Nobody sees prices until ${who} is verified — usually 1–2 business days.`
  }
  if (m > 0) {
    return `${n} ${n === 1 ? 'person' : 'people'} can see prices · ${m} paused.`
  }
  return n === 1
    ? '1 person can see your prices and your suppliers’ direct contacts.'
    : `${n} people can see your prices and your suppliers’ direct contacts.`
}

/* ───────────────────────── error classification ────────────────────────── */

/**
 * The server's `code`, pulled off an ApiError body.
 *
 * Every org screen branches on this and NEVER on the raw status: a 409 that
 * means "already used" and a 409 that means "you're in another company" need
 * different screens with different exits, and a bare "409" on the glass is the
 * dead end this whole pass exists to stop shipping.
 *
 * A `/org` 404 while `ORGS_LIVE` is off is indistinguishable from any other
 * 404 by design (the router 404s before `requireAuth`), so it reports
 * `not_found` and the caller renders "not available", never an error.
 */
export function orgErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null
  const body = err.body
  if (body && typeof body === 'object' && 'code' in body) {
    const code = (body as { code: unknown }).code
    if (typeof code === 'string' && code) return code
  }
  return err.status === 404 ? 'not_found' : null
}

/**
 * A sentence WE authored for a situation the server has no code for — the only
 * error message a screen may show that did not come from the API.
 *
 * It exists so `orgErrorMessage` can tell "a human wrote this for the user"
 * apart from "a TypeError escaped". Surfacing every `Error.message` would put
 * "x is not a function" in front of a diamond buyer; surfacing none of them
 * throws away the one place a multi-step client action has to explain itself
 * (resend: the old invite is revoked BEFORE the new one is minted, so a
 * failure halfway leaves the invitee with nothing and the owner needs to be
 * told exactly that).
 */
export class OrgActionMessage extends Error {}

/** The server's own human sentence, when it sent one. Prefer it: it knows
 *  which rule fired and it names the route that does work. */
export function orgErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body
    if (body && typeof body === 'object' && 'error' in body) {
      const msg = (body as { error: unknown }).error
      if (typeof msg === 'string' && msg.trim()) return msg
    }
    return fallback
  }
  if (err instanceof OrgActionMessage && err.message.trim()) return err.message
  return fallback
}

/** The `seats` block a 402 carries, so the paywall opens with no second
 *  round trip. Null when this was not a seat-limit refusal. */
export function seatLimitFrom(err: unknown): OrgSeatsLite | null {
  if (!(err instanceof ApiError) || err.status !== 402) return null
  const body = err.body as { seats?: Partial<OrgSeatsLite> } | null
  const s = body?.seats
  if (!s || typeof s !== 'object') return null
  const n = (v: unknown, d = 0) => (typeof v === 'number' && Number.isFinite(v) ? v : d)
  return {
    used: n(s.used), included: n(s.included), paid: n(s.paid),
    limit: n(s.limit), pendingInvites: n(s.pendingInvites),
  }
}

export interface OrgSeatsLite {
  used: number
  included: number
  paid: number
  limit: number
  pendingInvites: number
}
